import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { userManagementApi } from '../api/userManagement';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { txt } from '../utils/bilingualText';
import StatusBadge from '../components/common/StatusBadge';

const Queue = () => {
  const navigate = useNavigate();
  const { user, isSupervisor, isManagement, isAdmin } = useAuth();
  const t = useLang();
  const [approvalQueue, setApprovalQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const loadQueue = useCallback(async () => {
    try {
      setLoading(true);
      const response = await userManagementApi.getApprovalQueue();
      const queueData = response.queue || response.items || [];
      setApprovalQueue(queueData);
    } catch (error) {
      console.error('Failed to load approval queue:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isSupervisor() && !isManagement() && !isAdmin()) {
      navigate('/dashboard');
      return;
    }
    loadQueue();
  }, [isSupervisor, isManagement, isAdmin, navigate, loadQueue]);

  const handleApprovalAction = async (item, action, note = '', reason = '') => {
    setActionLoading(true);
    try {
      // Support both old format (string submissionId) and new format (full item object)
      const isItemObject = typeof item === 'object' && item !== null;
      const submissionId = isItemObject ? (item.submission_id || item.session_id) : item;
      const sessionId = isItemObject ? item.session_id : null;
      const source = isItemObject ? item.source : null;
      
      await userManagementApi.approvalAction({
        submission_id: submissionId,
        session_id: sessionId,
        source: source,
        action: action,
        note: note,
        reason: reason
      });
      
      // Reload queue after action
      await loadQueue();
    } catch (error) {
      console.error('Approval action failed:', error);
      alert('Failed to perform approval action. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const formatAge = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const now = new Date();
    const created = new Date(timestamp);
    const diffMs = now - created;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    return 'Recent';
  };

  const getRiskBadge = (riskLevel) => {
    const riskColors = {
      'low': 'bg-green-100 text-green-800',
      'moderate': 'bg-yellow-100 text-yellow-800', 
      'high': 'bg-red-100 text-red-800'
    };
    return riskColors[riskLevel] || 'bg-gray-100 text-gray-800';
  };

  const getSLAStatus = (slaHours) => {
    if (slaHours <= 2) return { color: 'text-red-600', text: `${slaHours}h left`, urgent: true };
    if (slaHours <= 8) return { color: 'text-yellow-600', text: `${slaHours}h left`, urgent: false };
    return { color: 'text-green-600', text: `${slaHours}h left`, urgent: false };
  };

  if (!isSupervisor() && !isManagement() && !isAdmin()) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="nexus-content overflow-hidden bg-white">
      
      {/* EMZI Header - Normalized with Dashboard */}
      <div className="px-5 py-4 bg-emzi-blue text-white flex-shrink-0 shadow-xl" style={{background: 'linear-gradient(to bottom, #0e67a2 0%, #0a5a8a 100%)'}}>
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center border border-white border-opacity-30 shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-black tracking-wide text-white" data-testid="queue-title">
                {t('queue.title')}
              </h1>
              <p className="text-sm text-white font-medium">
                {t('dashboard.mySubmissions')}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end space-y-1">
            {/* ISO Compliance Badge - Management & Admin Only */}
            {(isManagement() || isAdmin()) && (
              <span 
                className="text-white text-xs font-medium opacity-90"
                title="System certified under EMZI ISO-Aligned Compliance Protocol"
              >
                🟢 {t('dashboard.isoCompliance')}
              </span>
            )}
            <div className="flex gap-2">
              <Button 
                onClick={() => navigate('/dashboard')} 
                variant="outline"
                size="sm"
                className="border-2 border-white border-opacity-50 text-white hover:bg-white hover:bg-opacity-20 font-semibold px-3 py-1 text-xs touch-target transition-all duration-200 hover:scale-105"
                data-testid="back-to-dashboard-button"
              >
                ← {t('common.back')}
              </Button>
              <Button 
                onClick={loadQueue} 
                disabled={loading}
                variant="outline"
                size="sm"
                className="border-2 border-white border-opacity-50 text-white hover:bg-white hover:bg-opacity-20 font-semibold px-3 py-1 text-xs touch-target transition-all duration-200 hover:scale-105"
              >
                🔄
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto panel-scroll px-4 pt-3 pb-24">

        {/* Queue Statistics - EMZI Theme */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          <Card className="border-0" style={{boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.06)'}}>
            <CardHeader className="pb-2 text-center">
              <CardDescription className="font-bold text-gray-600 text-xs">Total</CardDescription>
              <CardTitle className="text-2xl font-black text-emzi-blue">{approvalQueue.length}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="border-0" style={{boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.06)'}}>
            <CardHeader className="pb-2 text-center">
              <CardDescription className="font-bold text-gray-600 text-xs">Urgent</CardDescription>
              <CardTitle className="text-2xl font-black text-red-500">
                {approvalQueue.filter(item => (item.sla_hours_remaining || 24) <= 2).length}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card className="border-0" style={{boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.06)'}}>
            <CardHeader className="pb-2 text-center">
              <CardDescription className="font-bold text-gray-600 text-xs">Risk</CardDescription>
              <CardTitle className="text-2xl font-black text-emzi-orange">
                {approvalQueue.filter(item => item.risk_level === 'high').length}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card className="border-0" style={{boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.06)'}}>
            <CardHeader className="pb-2 text-center">
              <CardDescription className="font-bold text-gray-600 text-xs">{t('profile.role')}</CardDescription>
              <CardTitle className="text-xl font-black text-gray-800">
                {t(`profile.roles.${user?.role || 'executor'}`)}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Queue Items - EMZI Theme */}
        <Card className="border-0" style={{boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 4px 16px rgba(0, 0, 0, 0.06)'}} data-testid="queue-items-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-black text-lg text-gray-900">{t('queue.pending')}</CardTitle>
            <CardDescription className="font-medium text-gray-600 text-sm">
              {t('queue.emptyPending')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-gray-600 font-medium">{t('queue.loadingQueue')}</div>
              </div>
            ) : approvalQueue.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">✅</div>
                <h3 className="text-xl font-black mb-3 text-green-600">{t('queue.allClear')}</h3>
                <p className="text-gray-600 font-medium">{t('queue.emptyPending')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {approvalQueue
                  .sort((a, b) => {
                    // Sort by urgency first, then by age
                    const aUrgent = (a.sla_hours_remaining || 24) <= 2;
                    const bUrgent = (b.sla_hours_remaining || 24) <= 2;
                    if (aUrgent && !bUrgent) return -1;
                    if (!aUrgent && bUrgent) return 1;
                    return new Date(b.created_at) - new Date(a.created_at);
                  })
                  .map((item) => {
                    const sla = getSLAStatus(item.sla_hours_remaining || 24);
                    return (
                      <div 
                        key={item.submission_id || item.session_id || item.id} 
                        data-testid={`queue-item-${item.submission_id || item.session_id}`}
                        className={`border rounded-lg p-6 bg-white transition-all duration-200 hover:shadow-md ${sla.urgent ? 'border-red-200 bg-red-50' : ''}`}
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <Badge variant="outline" className="text-xs font-medium">
                                {item.studio}
                              </Badge>
                              <StatusBadge status={item.submission_status || 'pending_approval'} size="xs" />
                              {item.video_status && (
                                <StatusBadge status={item.video_status === 'approved' ? 'video_approved' : item.video_status === 'rejected' ? 'video_rejected' : item.video_status} size="xs" />
                              )}
                              <Badge className={`text-xs ${getRiskBadge(item.risk_level)}`}>
                                {item.risk_level} risk
                              </Badge>
                              <span className={`text-sm font-bold ${sla.color}`}>
                                {sla.text}
                              </span>
                              {sla.urgent && (
                                <Badge className="bg-red-600 text-white text-xs animate-pulse">
                                  URGENT
                                </Badge>
                              )}
                            </div>
                            <h4 className="text-xl font-bold text-gray-900 mb-1">
                              {item.project_name || item.objective_type || item.title || 'Storyboard Submission'}
                            </h4>
                            <p className="text-gray-600 mb-2">
                              {t('queue.submittedBy')}: <span className="font-medium">{item.executor_name || 'Unknown'}</span> • {formatAge(item.created_at || item.submitted_at)}
                            </p>
                            <p className="text-sm text-gray-500">
                              📅 {item.submitted_at ? new Date(item.submitted_at).toLocaleString() : 'N/A'}
                            </p>
                            {item.description && (
                              <p className="text-sm text-gray-700 mt-2 p-2 bg-gray-50 rounded">
                                {txt(item.description)}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex gap-2 flex-wrap pt-2 border-t">
                          <Button
                            size="sm"
                            onClick={() => navigate(`/queue/review/${item.session_id}`)}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4"
                            data-testid={`queue-view-${item.submission_id || item.session_id}`}
                          >
                            View
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleApprovalAction(item, 'approve')}
                            className="bg-green-600 hover:bg-green-700 text-white px-4"
                            disabled={actionLoading}
                            data-testid={`queue-approve-${item.submission_id || item.session_id}`}
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="px-4 border-amber-400 text-amber-700 hover:bg-amber-50"
                            onClick={() => {
                              const note = prompt(t('queue.requestEdit'));
                              if (note && note.trim()) {
                                handleApprovalAction(item, 'request_edit', note.trim());
                              }
                            }}
                            disabled={actionLoading}
                            data-testid={`queue-edit-${item.submission_id || item.session_id}`}
                          >
                            Request Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300 hover:bg-red-50 px-4"
                            onClick={() => {
                              const reason = prompt(t('queue.rejectConfirm'));
                              if (reason && reason.trim()) {
                                handleApprovalAction(item, 'reject', '', reason.trim());
                              }
                            }}
                            disabled={actionLoading}
                            data-testid={`queue-reject-${item.submission_id || item.session_id}`}
                          >
                            Reject
                          </Button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Queue;